// TranscriptSync Integrations Store Module
import TranscriptSyncAPI from '../../services/TranscriptSyncAPI'

export default {
  namespaced: true,
  
  state: () => ({
    integrations: [],
    currentIntegration: null,
    loading: false,
    error: null,
    drawerOpen: false
  }),
  
  getters: {
    integrations: state => state.integrations,
    currentIntegration: state => state.currentIntegration,
    isLoading: state => state.loading,
    error: state => state.error,
    isDrawerOpen: state => state.drawerOpen,
    
    getIntegrationById: (state) => (id) => {
      return state.integrations.find(integration => integration.id === id)
    },
    
    getIntegrationBySlug: (state) => (slug) => {
      return state.integrations.find(integration => integration.slug === slug)
    },
    
    enabledIntegrations: (state) => {
      return state.integrations.filter(integration => 
        integration.platform?.is_enabled || integration.is_enabled
      )
    },
    
    connectedIntegrations: (state) => {
      return state.integrations.filter(integration => 
        integration.connected || integration.platform?.is_connected
      )
    }
  },
  
  mutations: {
    SET_INTEGRATIONS(state, integrations) {
      state.integrations = integrations
    },
    
    SET_CURRENT_INTEGRATION(state, integration) {
      state.currentIntegration = integration
    },
    
    UPDATE_INTEGRATION(state, updatedIntegration) {
      // Format the integration data similar to Dashboard
      const formattedIntegration = {
        id: updatedIntegration.id || updatedIntegration.platform?.integration_id,
        slug: updatedIntegration.slug || updatedIntegration.platform?.slug,
        name: updatedIntegration.name || updatedIntegration.platform?.name,
        description: updatedIntegration.description || updatedIntegration.platform?.description,
        connected: updatedIntegration.connected !== undefined ? updatedIntegration.connected : (updatedIntegration.platform?.is_connected || false),
        syncs: updatedIntegration.syncs !== undefined ? updatedIntegration.syncs : (updatedIntegration.platform?.total_syncs || 0),
        lastSync: updatedIntegration.lastSync || 'Never',
        color: updatedIntegration.color || updatedIntegration.platform?.brand_color || '#6B7280',
        logo: updatedIntegration.logo || updatedIntegration.platform?.logo_url,
        logoSvg: updatedIntegration.logoSvg || updatedIntegration.platform?.logo_svg,
        status: updatedIntegration.status || (updatedIntegration.platform?.is_enabled ? 'active' : 'disabled'),
        healthScore: updatedIntegration.healthScore || updatedIntegration.platform?.health_score || null,
        platform: updatedIntegration.platform || updatedIntegration
      }
      
      // Use slug to find the index since it's unique
      
      const index = state.integrations.findIndex(i => i.slug === formattedIntegration.slug)
      //console.log('--slugs-->',index,formattedIntegration.slug,state.integrations );
      if (index !== -1) {
        state.integrations.splice(index, 1, formattedIntegration)
      } else {
        // If not found, add it
        state.integrations.push(formattedIntegration)
      }
      
      // Update current integration if it's the same
      if (state.currentIntegration?.slug === formattedIntegration.slug) {
        state.currentIntegration = formattedIntegration
      }
    },
    
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    
    SET_ERROR(state, error) {
      state.error = error
    },
    
    SET_DRAWER_OPEN(state, isOpen) {
      state.drawerOpen = isOpen
    },
    
    UPDATE_INTEGRATION_STATUS(state, { id, is_enabled }) {
      const integration = state.integrations.find(i => i.id === id)
      if (integration) {
        if (integration.platform) {
          integration.platform.is_enabled = is_enabled
        }
        integration.status = is_enabled ? 'active' : 'disabled'
      }
      
      if (state.currentIntegration?.id === id) {
        if (state.currentIntegration.platform) {
          state.currentIntegration.platform.is_enabled = is_enabled
        }
        state.currentIntegration.status = is_enabled ? 'active' : 'disabled'
      }
    }
  },
  
  actions: {
    async fetchIntegrations({ commit }) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      try {
        const response = await TranscriptSyncAPI.getDashboardIntegrations()
        
        if (response.success && response.data) {
          const platforms = response.data.platforms || [];
          
          // Format platforms to integrations like Dashboard does
          const formattedIntegrations = platforms.map(platform => ({
            id: platform.id,
            slug: platform.slug,
            name: platform.name,
            description: platform.description,
            connected: platform.is_connected || false,
            syncs: platform.total_syncs || 0,
            lastSync: platform.last_sync_at ? 
              new Date(platform.last_sync_at).toLocaleString() : 
              'Never',
            color: platform.brand_color || '#6B7280',
            logo: platform.logo_url,
            logoSvg: platform.logo_svg,
            status: platform.status || (platform.is_enabled ? 'active' : 'disabled'),
            healthScore: platform.health_score,
            platform: platform // Keep full platform data
          }));
          
          commit('SET_INTEGRATIONS', formattedIntegrations)
          return formattedIntegrations
        }
        
        // Fallback if structure is different
        const integrations = response.data || response
        commit('SET_INTEGRATIONS', integrations)
        return integrations
      } catch (error) {
        console.error('Failed to fetch integrations:', error)
        commit('SET_ERROR', error.message || 'Failed to fetch integrations')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async initializePlatform({ commit, dispatch }, platform) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      try {
        const response = await TranscriptSyncAPI.initializePlatform(platform)
        
        if (response.success && response.data) {
          // Format and update the new integration
          const newIntegration = response.data;
          const formattedIntegration = {
            id: newIntegration.id,
            slug: newIntegration.slug,
            name: newIntegration.name,
            description: newIntegration.description,
            connected: newIntegration.is_connected || false,
            syncs: newIntegration.total_syncs || 0,
            lastSync: newIntegration.last_sync_at ? 
              new Date(newIntegration.last_sync_at).toLocaleString() : 
              'Never',
            color: newIntegration.brand_color || '#6B7280',
            logo: newIntegration.logo_url,
            logoSvg: newIntegration.logo_svg,
            status: newIntegration.status || 'active',
            healthScore: newIntegration.health_score,
            platform: newIntegration
          };
          
          // Update the store
          commit('UPDATE_INTEGRATION', formattedIntegration)
          return formattedIntegration
        }
        
        throw new Error(response.message || 'Failed to initialize platform')
      } catch (error) {
        console.error('Failed to initialize platform:', error)
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async updateIntegration({ commit }, { id, data }) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      try {
        const response = await TranscriptSyncAPI.updateIntegration(id, data)
        
        // Handle the response which should have the full integration object
        let updatedIntegration;
        if (response.data) {
          updatedIntegration = response.data
        } else if (response.success && response.data) {
          updatedIntegration = response.data
        } else {
          updatedIntegration = response
        }
        
        commit('UPDATE_INTEGRATION', updatedIntegration)
        return updatedIntegration
      } catch (error) {
        console.error('Failed to update integration:', error)
        commit('SET_ERROR', error.message || 'Failed to update integration')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async toggleIntegration({ commit }, { id, enabled }) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      try {
        // Use the update endpoint with just is_enabled field
        const response = await TranscriptSyncAPI.updateIntegration(id, {
          is_enabled: enabled
        })
        
        // Handle the response which should have the full integration object
        let updatedIntegration;
        if (response.data) {
          updatedIntegration = response.data
        } else if (response.success && response.data) {
          updatedIntegration = response.data
        } else {
          updatedIntegration = response
        }
        
        // Update with the full integration object returned from backend
        commit('UPDATE_INTEGRATION', updatedIntegration)
        return updatedIntegration
      } catch (error) {
        console.error('Failed to toggle integration:', error)
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async testIntegrationConnection({ commit }, id) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      try {
        const response = await TranscriptSyncAPI.testConnection(id)
        return response
      } catch (error) {
        console.error('Failed to test connection:', error)
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    openDrawer({ commit }, integration) {
      commit('SET_CURRENT_INTEGRATION', integration)
      commit('SET_DRAWER_OPEN', true)
    },
    
    closeDrawer({ commit }) {
      commit('SET_DRAWER_OPEN', false)
      // Don't clear current integration immediately for animation purposes
      setTimeout(() => {
        commit('SET_CURRENT_INTEGRATION', null)
      }, 300)
    }
  }
}